"use client"

import { isClientSendable } from "@/lib/notifications/templates"

// ============================================================
// How a client asks for someone else to be notified.
// ------------------------------------------------------------
// It does not write the row and it does not write the words. It names a type
// and some records, and app/api/notifications/create decides whether the
// person asking is allowed and what the notification says.
//
// Deliberately NOT throwing. A notification is a side effect of something the
// person actually came to do — sending a request, answering a post — and that
// action must not fail because the notification did. It returns a boolean for
// callers who want to know, and most do not.
// ============================================================

export interface NotifyInput {
  /** Who is being told. */
  recipient: string
  /** Which template runs. Must be one the server allows a client to send. */
  type: string
  /** Named records the template may mention. Never words. */
  shopId?: string | null
  jobId?: string | null
  planName?: string | null
  amountCents?: number | null
  /** A person's own words. Shown in the app, never on a lock screen, and only
   *  accepted for the few types where the product needs it. */
  note?: string | null
  /** Ids the app reads when the notification is opened. Never rendered. */
  metadata?: Record<string, unknown>
}

/**
 * Shaped like a Supabase result on purpose — `{ error }`, null when it worked.
 * The 29 call sites this replaces all destructure that, and matching it keeps
 * the migration mechanical instead of 29 chances to change behaviour by hand.
 */
export interface NotifyResult {
  error: { message: string } | null
}

export async function notify(input: NotifyInput): Promise<NotifyResult> {
  if (!input.recipient || !isClientSendable(input.type)) {
    return { error: { message: `not a client-sendable notification: ${input.type}` } }
  }
  try {
    const res = await fetch("/api/notifications/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    })
    if (res.ok) return { error: null }
    return { error: { message: `notify failed: ${res.status}` } }
  } catch (err) {
    return { error: { message: (err as Error)?.message || "notify failed" } }
  }
}
